import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Megaphone, ChartBar } from "@phosphor-icons/react";
import { Badge } from "flowbite-react";
import { cn } from "../../../../lib/utils";
import { CampaignFunnelData, CampaignPlatform } from "../../interface/shared";
import { PLATFORM_ICON_MAP } from "../../../../shared/constants";

interface CampaignPerformanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  campaignName: string;
  platform: CampaignPlatform;
  status: string;
  displayStatus?: string;
  settings: { label: string; value: string | number; highlight?: boolean }[];
  performanceMetrics: { label: string; value: string | number }[];
  funnelData?: CampaignFunnelData;
  metadata?: { label: string; value: string }[];
  footerActions?: React.ReactNode;
}

const CampaignPerformanceDrawer: React.FC<CampaignPerformanceDrawerProps> = ({
  isOpen,
  onClose,
  campaignName,
  platform,
  status,
  displayStatus,
  settings,
  performanceMetrics,
  funnelData,
  metadata,
  footerActions,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end overflow-hidden pointer-events-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/20 backdrop-blur-[2px] pointer-events-auto"
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300, mass: 0.8 }}
            className="relative w-full max-w-xl h-full bg-white dark:bg-neutral-900 shadow-[-20px_0_50px_rgba(0,0,0,0.1)] flex flex-col pointer-events-auto overflow-hidden border-l border-gray-100 dark:border-neutral-700"
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 bg-white dark:bg-neutral-900 border-b border-gray-100 dark:border-neutral-700 sticky top-0 z-10 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center border border-gray-100 dark:border-neutral-700 shadow-sm flex-shrink-0">
                  {PLATFORM_ICON_MAP[platform] ? <img
                    src={PLATFORM_ICON_MAP[platform]}
                    alt={platform}
                    className="w-4 h-4 object-contain"
                  /> : <Megaphone size={22} className="text-gray-400" />}
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                    {campaignName}
                  </h2>
                  <div className="flex items-center gap-2">
                    <Badge
                      color={status === "ENABLED" || status === "ACTIVE" ? "success" : "warning"}
                      size="xs"
                      className="rounded-full px-2 py-0"
                    >
                      {displayStatus || status}
                    </Badge>
                    <Badge color="info" size="xs" className="rounded-full px-2 py-0 uppercase text-[9px] font-bold">
                      {platform.replace("_", " ")}
                    </Badge>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors group"
              >
                <X size={24} className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/30 dark:bg-neutral-950 custom-scrollbar">
              {/* Settings Section */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-neutral-700 flex flex-col gap-5">
                <div className="flex items-center gap-2 border-b border-gray-50 dark:border-neutral-700 pb-3">
                  <Megaphone size={18} className="text-gray-500 dark:text-gray-400" weight="duotone" />
                  <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 uppercase tracking-tighter">Campaign Settings</h3>
                </div>
                <div className="space-y-4">
                  {settings.map((item, idx) => (
                    <InfoRow key={idx} label={item.label} value={item.value} highlight={item.highlight} />
                  ))}
                </div>
              </div>

              {/* Performance Section */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-neutral-700 flex flex-col gap-5">
                <div className="flex items-center gap-2 border-b border-gray-50 dark:border-neutral-700 pb-2">
                  <ChartBar size={18} className="text-gray-500 dark:text-gray-400" weight="duotone" />
                  <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 uppercase tracking-tighter">Performance Highlights</h3>
                </div>
                <div className="space-y-4">
                  {performanceMetrics.map((item, idx) => (
                    <PerformanceRow key={idx} label={item.label} value={item.value} />
                  ))}
                </div>
              </div>

              {/* Conversion Funnel Section */}
              {funnelData && (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-neutral-700 flex flex-col gap-4">
                  <div className="flex items-center gap-2 border-b border-gray-50 dark:border-neutral-700 pb-2">
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 uppercase tracking-tighter">Conversion Funnel</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-8">
                    <FunnelRow
                      icon="👁️"
                      label="Impressions"
                      value={funnelData.impressions}
                      color="bg-blue-500"
                      percentage={funnelData.impressions}
                    />
                    <FunnelRow
                      icon="👆"
                      label="Clicks"
                      value={funnelData.clicks}
                      color="bg-orange-400"
                      percentage={funnelData.impressions > 0 ? (funnelData.clicks / funnelData.impressions) * 100 : 0}
                    />
                    <FunnelRow
                      icon="📲"
                      label="Installs"
                      value={funnelData.installs}
                      color="bg-indigo-600"
                      percentage={funnelData.impressions > 0 ? (funnelData.installs / funnelData.impressions) * 100 : 0}
                    />
                    <FunnelRow
                      icon="🛒"
                      label="Purchases"
                      value={funnelData.purchases}
                      color="bg-emerald-500"
                      percentage={funnelData.impressions > 0 ? (funnelData.purchases / funnelData.impressions) * 100 : 0}
                    />
                  </div>
                </div>
              )}

              {/* Metadata */}
              {metadata && metadata.length > 0 && (
                <div className="py-2 px-1">
                  <div className="flex flex-col gap-1 text-[11px] text-gray-400 uppercase tracking-widest font-bold">
                    {metadata.map((item, idx) => (
                      <span key={idx}>{item.label}: {item.value}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            {footerActions && (
              <div className="p-4 bg-white dark:bg-neutral-900 border-t border-gray-100 dark:border-neutral-700 mt-auto shadow-[0_-10px_20px_rgba(0,0,0,0.02)] sticky bottom-0 z-10 flex items-center gap-2">
                {footerActions}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// Helper Components
const InfoRow = ({ label, value, highlight = false }: { label: string; value: string | number; highlight?: boolean }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-50 dark:border-neutral-700 last:border-0 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors px-1 rounded-md">
    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</span>
    <span className={cn("text-xs font-bold", highlight ? "text-primary-600 dark:text-primary-400" : "text-gray-800 dark:text-gray-100")}>{value}</span>
  </div>
);

const PerformanceRow = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-50 dark:border-neutral-700 last:border-0 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors px-1 rounded-md">
    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</span>
    <span className="text-xs font-bold text-gray-800 dark:text-gray-100">{value}</span>
  </div>
);

const FunnelRow = ({ icon, label, value, color, percentage }: { icon: string; label: string; value: number; color: string; percentage: number }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm">{icon}</span>
        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{label}</span>
      </div>
      <span className="text-xs font-bold text-gray-900 dark:text-gray-100">{value.toLocaleString()}</span>
    </div>
    <div className="w-full h-1.5 bg-gray-100 dark:bg-neutral-800 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        className={cn("h-full rounded-full", color)}
      />
    </div>
  </div>
);

export default CampaignPerformanceDrawer;
